/**
 * The job form (PF-2, fieldiq-api#1200): full page for New job, an inline
 * panel on the job's own page for Edit. Never a modal — a job is a record.
 *
 * Chainage is typed and sent in the tenant's units (`meta.units.chainage`); the
 * API converts. A blank is sent as null, never 0. On edit only the keys that
 * changed are sent (PATCH keeps the rest). Status is not edited here: on a new
 * job it is Draft or Scheduled, and afterwards it moves by the status actions.
 *
 * The site list is the chosen customer's; the segment list is the chosen
 * pipeline's. A change of customer clears a site of another customer, so the
 * API's site_customer_mismatch is the exception, not the routine.
 */
import React, { useMemo, useState } from 'react';
import { usePfSite, usePfSites } from '../../hooks/usePipelineFacility';
import { blankToNull, toInput } from '../../lib/pipeline-facility';
import type { PfCatalog, PfJob, PfJobWrite, PfMeta } from '../../types/pipeline-facility';
import { PF_DATE_INPUT, PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError } from './PfBits';

interface FormState {
  customer_id: string;
  site_id: string;
  segment_id: string;
  from_chainage: string;
  to_chainage: string;
  module: string;
  code: string;
  ticket_mode: string;
  supervisor_id: string;
  scheduled_start: string;
  scheduled_end: string;
  scope: string;
  po_number: string;
  afe_number: string;
  cost_code: string;
  location_name: string;
  notes: string;
  status: string;
}

const fromJob = (j: PfJob | null): FormState => ({
  customer_id: j?.customerId ?? '',
  site_id: j?.siteId ?? '',
  segment_id: j?.segmentId ?? '',
  from_chainage: toInput(j?.fromChainage),
  to_chainage: toInput(j?.toChainage),
  module: j?.module ?? 'dig',
  code: j?.code ?? '',
  ticket_mode: j?.ticketMode ?? '',
  supervisor_id: j?.supervisorId ?? '',
  scheduled_start: j?.scheduledStart ?? '',
  scheduled_end: j?.scheduledEnd ?? '',
  scope: j?.scope ?? '',
  po_number: j?.poNumber ?? '',
  afe_number: j?.afeNumber ?? '',
  cost_code: j?.costCode ?? '',
  location_name: j?.locationName ?? '',
  notes: j?.notes ?? '',
  status: 'draft',
});

const TEXT_KEYS: (keyof FormState)[] = [
  'segment_id', 'from_chainage', 'to_chainage', 'code', 'supervisor_id', 'scheduled_start', 'scheduled_end',
  'scope', 'po_number', 'afe_number', 'cost_code', 'location_name', 'notes',
];

/** The write body: every field on create, the changed ones on edit. */
export function jobWriteBody(form: FormState, original: FormState | null): PfJobWrite {
  const out: Record<string, unknown> = {};
  const changed = (k: keyof FormState) => !original || form[k] !== original[k];
  if (changed('customer_id')) out.customer_id = form.customer_id;
  if (changed('site_id')) out.site_id = form.site_id;
  if (changed('module') && form.module) out.module = form.module;
  if (changed('ticket_mode') && form.ticket_mode) out.ticket_mode = form.ticket_mode;
  for (const k of TEXT_KEYS) if (changed(k)) {
    const v = blankToNull(form[k]);
    if (original || v !== null) out[k] = v;
  }
  if (!original) out.status = form.status;
  return out as PfJobWrite;
}

export function JobForm({
  job,
  catalog,
  meta,
  customers,
  users,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  job: PfJob | null;
  catalog: PfCatalog | null;
  meta: PfMeta | null;
  customers: { id: string; name: string }[];
  users: { id: string; name: string }[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: PfJobWrite) => void;
}) {
  const original = useMemo(() => (job ? fromJob(job) : null), [job]);
  const [form, setForm] = useState<FormState>(() => fromJob(job));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = (k: keyof FormState, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const sites = usePfSites(form.customer_id ? { customerId: form.customer_id } : undefined);
  const siteDetail = usePfSite(form.site_id || undefined);
  const segments = siteDetail.data?.data?.kind === 'pipeline' ? siteDetail.data.data.segments ?? [] : [];
  const chainage = meta?.units?.chainage ?? null;
  const unit = (u: string | null) => (u ? ` (${u})` : '');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.customer_id) return setLocalError('Choose the customer.');
    if (!form.site_id) return setLocalError('Choose the site.');
    if (!!form.from_chainage.trim() !== !!form.to_chainage.trim()) return setLocalError('Enter both chainages, or neither.');
    if (form.scheduled_start && form.scheduled_end && form.scheduled_end < form.scheduled_start)
      return setLocalError('The scheduled end cannot be before the start.');
    const body = jobWriteBody(form, original);
    if (original && Object.keys(body).length === 0) return onCancel();
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="pf-job-form">
      <PfFormError message={localError ?? error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PfField label="Customer">
          <select
            className={PF_INPUT}
            aria-label="Customer"
            value={form.customer_id}
            onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value, site_id: '', segment_id: '' }))}
          >
            <option value="">Choose…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Site" hint={form.customer_id ? undefined : 'Choose the customer first'}>
          <select
            className={PF_INPUT}
            aria-label="Site"
            value={form.site_id}
            disabled={!form.customer_id}
            onChange={(e) => setForm((f) => ({ ...f, site_id: e.target.value, segment_id: '' }))}
          >
            <option value="">{sites.isLoading ? 'Loading…' : 'Choose…'}</option>
            {(sites.data?.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </PfField>
        {segments.length > 0 && (
          <PfField label="Segment">
            <select className={PF_INPUT} aria-label="Segment" value={form.segment_id} onChange={(e) => set('segment_id', e.target.value)}>
              <option value="">Whole line</option>
              {segments.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label || `${toInput(s.fromChainage)}–${toInput(s.toChainage)}${chainage ? ` ${chainage}` : ''}`}
                </option>
              ))}
            </select>
          </PfField>
        )}
        <PfField label={`From chainage${unit(chainage)}`}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="From chainage" value={form.from_chainage} onChange={(e) => set('from_chainage', e.target.value)} />
        </PfField>
        <PfField label={`To chainage${unit(chainage)}`}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="To chainage" value={form.to_chainage} onChange={(e) => set('to_chainage', e.target.value)} />
        </PfField>
        <PfField label="Work type">
          <select className={PF_INPUT} aria-label="Work type" value={form.module} onChange={(e) => set('module', e.target.value)}>
            {(catalog?.modules ?? [{ value: 'dig', label: 'Integrity / dig' }]).map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Code">
          <select className={PF_INPUT} aria-label="Code" value={form.code} onChange={(e) => set('code', e.target.value)}>
            <option value="">Company default</option>
            {(catalog?.codes ?? []).map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Tickets">
          <select className={PF_INPUT} aria-label="Tickets" value={form.ticket_mode} onChange={(e) => set('ticket_mode', e.target.value)}>
            <option value="">Customer default</option>
            {(catalog?.ticketModes ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Supervisor">
          <select className={PF_INPUT} aria-label="Supervisor" value={form.supervisor_id} onChange={(e) => set('supervisor_id', e.target.value)}>
            <option value="">None</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Scheduled start">
          <input type="date" className={PF_DATE_INPUT} aria-label="Scheduled start" value={form.scheduled_start} onChange={(e) => set('scheduled_start', e.target.value)} />
        </PfField>
        <PfField label="Scheduled end">
          <input type="date" className={PF_DATE_INPUT} aria-label="Scheduled end" value={form.scheduled_end} onChange={(e) => set('scheduled_end', e.target.value)} />
        </PfField>
        <PfField label="Location">
          <input className={PF_INPUT} aria-label="Location" value={form.location_name} onChange={(e) => set('location_name', e.target.value)} />
        </PfField>
        <PfField label="PO number">
          <input className={PF_INPUT} aria-label="PO number" value={form.po_number} onChange={(e) => set('po_number', e.target.value)} />
        </PfField>
        <PfField label="AFE number">
          <input className={PF_INPUT} aria-label="AFE number" value={form.afe_number} onChange={(e) => set('afe_number', e.target.value)} />
        </PfField>
        <PfField label="Cost code">
          <input className={PF_INPUT} aria-label="Cost code" value={form.cost_code} onChange={(e) => set('cost_code', e.target.value)} />
        </PfField>
        {!job && (
          <PfField label="Start as">
            <select className={PF_INPUT} aria-label="Start as" value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </PfField>
        )}
      </div>
      <PfField label="Scope">
        <textarea className={`${PF_INPUT} h-20 py-2`} aria-label="Scope" value={form.scope} onChange={(e) => set('scope', e.target.value)} />
      </PfField>
      <PfField label="Notes">
        <textarea className={`${PF_INPUT} h-20 py-2`} aria-label="Notes" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      </PfField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={PF_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
